import { useState, useCallback, useRef, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import AppLayout from '../components/AppLayout';
import SearchFilters from '../components/SearchFilters';
import RestaurantList from '../components/RestaurantList';
import MapView, { MapViewHandle } from '../components/MapView';
import { useRestaurants } from '../hooks/useRestaurants';
import { usePreferences } from '../hooks/usePreferences';
import { useAuth } from '../hooks/useAuth';
import { useProfiles } from '../hooks/useProfiles';
import { useFavorites } from '../hooks/useFavorites';
import { SortBy } from '../types';

/** Default search point: Kutteritie 2, 33960 Pirkkala */
const DEFAULT_LAT = 61.464;
const DEFAULT_LNG = 23.653;

export default function HomePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { preferences, setRadius, setCategories } = usePreferences();
  const [query, setQuery] = useState('');
  const [sortBy, setSortBy] = useState<SortBy>('distance');
  const mapRef = useRef<MapViewHandle>(null);

  // Read initial search point from URL params (set by ProfilesPage on selection)
  const urlLat = searchParams.get('lat');
  const urlLng = searchParams.get('lng');
  const initialFromUrl = urlLat && urlLng
    ? { lat: parseFloat(urlLat), lng: parseFloat(urlLng) }
    : null;

  const [searchPoint, setSearchPoint] = useState<{ lat: number; lng: number } | null>(initialFromUrl);

  // Clear URL params after reading them (keep URL clean)
  useEffect(() => {
    if (urlLat && urlLng) {
      setSearchParams({}, { replace: true });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLocate = useCallback((lat: number, lng: number) => {
    mapRef.current?.focusRestaurant(lat, lng);
  }, []);

  const { user, isAuthenticated, isLoading: isAuthLoading, login, logout } = useAuth();
  const { profiles } = useProfiles(isAuthenticated);
  const { favoriteIds, toggleFavorite } = useFavorites(isAuthenticated);

  // Apply active profile's coordinates as search point (when no URL override)
  useEffect(() => {
    if (initialFromUrl) return; // URL params take precedence on first load
    if (preferences.lastProfileId && profiles.length > 0) {
      const active = profiles.find((p) => p.profileId === preferences.lastProfileId);
      if (active) {
        setSearchPoint(active.coordinates);
        return;
      }
    }
    if (!preferences.lastProfileId) {
      setSearchPoint({ lat: DEFAULT_LAT, lng: DEFAULT_LNG });
    }
  }, [preferences.lastProfileId, profiles]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set default while profiles are loading (only if no active profile and no URL params)
  useEffect(() => {
    if (!preferences.lastProfileId && !initialFromUrl) {
      setSearchPoint({ lat: DEFAULT_LAT, lng: DEFAULT_LNG });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveSearchPoint = searchPoint ?? { lat: DEFAULT_LAT, lng: DEFAULT_LNG };

  const { restaurants, isLoading, total } = useRestaurants({
    lat: effectiveSearchPoint.lat,
    lng: effectiveSearchPoint.lng,
    radius: preferences.radiusKm,
    query,
    categories: preferences.selectedCategories,
    sortBy,
    favoriteIds,
  });

  return (
    <AppLayout
      user={user}
      isAuthenticated={isAuthenticated}
      isAuthLoading={isAuthLoading}
      onLogin={login}
      onLogout={logout}
    >
      <SearchFilters
        query={query}
        onQueryChange={setQuery}
        radiusKm={preferences.radiusKm}
        onRadiusChange={setRadius}
        selectedCategories={preferences.selectedCategories}
        onCategoriesChange={setCategories}
        sortBy={sortBy}
        onSortChange={setSortBy}
      />

      <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, mt: 2, height: { md: 'calc(100vh - 200px)' } }}>
        <Box sx={{ flex: 1, minWidth: 0, overflow: 'auto', height: { xs: 'auto', md: '100%' } }}>
          <RestaurantList
            restaurants={restaurants}
            isLoading={isLoading}
            total={total}
            favoriteIds={favoriteIds}
            onToggleFavorite={isAuthenticated ? toggleFavorite : undefined}
            onLocate={handleLocate}
          />
        </Box>
        <Box
          sx={{
            flex: 1,
            height: { xs: 300, md: '100%' },
          }}
        >
          <MapView
            ref={mapRef}
            restaurants={restaurants}
            searchPoint={effectiveSearchPoint}
          />
        </Box>
      </Box>
    </AppLayout>
  );
}
